import { IWFStep, IWorkFlow, IWorkFlowBaseInfo } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { ScreenService } from "../../services/screen-service";

// Single source of the "used in a process" set behind the action-editor warning (KOA-18085). Unions
// step actions (client-side, fresh) with user-task SCREEN actions (backend, cached per project — its
// scan is remote-DB bound). Ids are lower-cased so C# `Guid.ToString()` matches client ids.
interface ScreenActionIdsCache {
  projectId: string;
  promise: Promise<string[]>;
}

let screenActionIdsCache: ScreenActionIdsCache | undefined;

async function fetchScreenActionIds(projectId: string): Promise<string[]> {
  const response: AxiosResponse<string[]> = await ScreenService.GetWorkflowScreenActionDefinitionIds(projectId);
  const isOk: boolean = response?.status === 200 && Array.isArray(response.data);
  return isOk ? response.data : [];
}

function getScreenActionIds(projectId: string, refresh: boolean): Promise<string[]> {
  const isCached: boolean = screenActionIdsCache !== undefined && screenActionIdsCache.projectId === projectId;
  if (!refresh && isCached && screenActionIdsCache) {
    return screenActionIdsCache.promise;
  }
  const promise: Promise<string[]> = fetchScreenActionIds(projectId).catch((): string[] => []);
  screenActionIdsCache = { projectId, promise };
  return promise;
}

// Fire-and-forget cache refresh; call on mount of an action-editing surface so the banner is warm
// before an editor opens (avoids the late pop-in) and picks up screen-usage changes on re-entry.
export function prefetchProcessUsedActionIds(projectId: string | undefined): void {
  if (projectId) {
    void getScreenActionIds(projectId, true);
  }
}

function collectStepActionIdsInto(steps: IWFStep[] | undefined, target: string[]): void {
  if (!steps) return;
  for (const step of steps) {
    step?.actions?.forEach((action) => {
      if (action?.actionDefinitionId != null) target.push(action.actionDefinitionId);
    });
    // A subprocess's steps belong to the same process, so an action attached there is used in it too.
    collectStepActionIdsInto(step?.subprocess?.steps, target);
  }
}

// Action ids attached to workflow steps, recursing into subprocesses. Pure — exported for tests.
export function collectStepActionIds(workFlowList: IWorkFlowBaseInfo[] | undefined): string[] {
  const ids: string[] = [];
  for (const wf of workFlowList ?? []) {
    // workFlowList is typed IWorkFlowBaseInfo but carries `nodes` at runtime — cast, as data-diagram does.
    collectStepActionIdsInto((wf as IWorkFlow).nodes?.steps, ids);
  }
  return ids;
}

export async function getProcessUsedActionIds(
  workFlowList: IWorkFlowBaseInfo[] | undefined,
  projectId: string | undefined
): Promise<Set<string>> {
  const usedActionIds: Set<string> = new Set<string>(collectStepActionIds(workFlowList).map((id) => id.toLowerCase()));

  if (projectId) {
    const screenActionIds: string[] = await getScreenActionIds(projectId, false);
    screenActionIds.forEach((id: string) => usedActionIds.add(id.toLowerCase()));
  }

  return usedActionIds;
}
